import type { MutableRefObject } from "react";
import type { AppDispatch } from "@store";
import type { FroalaEditorInstance } from "./types";
import { editEntity } from "@store/story";
import { blocksToTemplateInDom } from "@/shared/utils/variable-block";
import {
  pillsToTemplateInDom,
  repairSplitVariableTokens,
} from "@/shared/utils/variable-pill";
import { unwrapElement } from "../../utils";
import { normalizeInlineFormatting } from "./html-normalizer";
import { ZWS } from "./pending-format-state";

/** Strip all Zero-Width Space characters used as formatting anchors */
const stripZWS = (doc: Document) => {
  // Collect text nodes first, then mutate (avoids modifying DOM during TreeWalker iteration)
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (node.textContent?.includes(ZWS)) {
      textNodes.push(node as Text);
    }
    node = walker.nextNode();
  }
  for (const textNode of textNodes) {
    try {
      textNode.textContent = textNode.textContent?.replaceAll(ZWS, "") ?? "";
    } catch (err) {
      console.warn("[Froala] Failed to strip ZWS from text node:", err);
    }
  }
};

/**
 * Serialise the editor's HTML back to store content and commit it.
 *
 * `undoRedoEchoRef` (optional) holds the content string an undo/redo
 * write-back just pushed into the editor via `html.set()`. Froala's debounced
 * save echoes that content straight back here, and recording the echo as a
 * history frame wiped `history.future` — redo went dead after an undo,
 * intermittently, because the 500 ms save interval raced the 500 ms
 * `isInRedoUndo` reset. An echo changes nothing, so it commits with
 * `skipHistory`. Compared by content rather than off the flag/timer: it holds
 * however long the save takes to arrive.
 */
export const createUpdateChanges = (
  id: string,
  froalaInstance: MutableRefObject<FroalaEditorInstance | null>,
  hasContentChanged: MutableRefObject<boolean>,
  dispatch: AppDispatch,
  customOnUpdate?: (html: string) => void,
  undoRedoEchoRef?: MutableRefObject<string | null>,
  isInRedoUndoRef?: MutableRefObject<boolean>,
) => {
  return (html: string) => {
    if (!froalaInstance.current || !hasContentChanged.current) return;
    hasContentChanged.current = false;

    const tmpTree = new DOMParser().parseFromString(html, "text/html");

    const tmpHis = tmpTree.querySelectorAll(".fr-tmp-highlight");
    for (const h of Array.from(tmpHis)) {
      unwrapElement(h);
    }

    const his = tmpTree.querySelectorAll("[data-highlight-active]");
    const rkpis = tmpTree.querySelectorAll("[data-rkpi-active]");
    for (const h of Array.from(his)) {
      h.setAttribute("data-highlight-active", "false");
    }
    for (const h of Array.from(rkpis)) {
      h.setAttribute("data-rkpi-active", "false");
      h.innerHTML = h.getAttribute("data-rkpi-number-from") ?? "0";
    }
    const allLinks = tmpTree.querySelectorAll("a");
    for (const link of Array.from(allLinks)) {
      const href = link.getAttribute("href") ?? "";

      if (/javascript(:|%3A)void\(0\)/gm.test(href)) {
        // Normalize encoded colon back to plain javascript:void(0);
        link.setAttribute("href", "javascript:void(0);");
        link.removeAttribute("target");
        link.removeAttribute("data-open-in-popup");
      } else {
        link.removeAttribute("data-anchor-trigger");
      }
    }

    // Clean up ZWS formatting anchors before saving
    stripZWS(tmpTree);

    // Normalize redundant inline formatting (Phase 4)
    normalizeInlineFormatting(tmpTree.body);

    // Convert any variable pills/blocks back to {{name}} / {{{name}}} tokens
    pillsToTemplateInDom(tmpTree.body);
    blocksToTemplateInDom(tmpTree.body);

    // Heal variable tokens that inline formatting split across element
    // boundaries (e.g. bolding part of a {{var}}), which would otherwise leave
    // the variable unrecognized/broken on the next render. Bug 12714308261.
    repairSplitVariableTokens(tmpTree.body);

    const content = tmpTree.body.innerHTML;

    // Undo/redo write-back echo (see the doc comment above): the same content
    // the undo just applied, coming back out of Froala's save. The echo is
    // ARMED by the undo/redo write-back and CONSUMED by the first save that
    // follows it, whatever that save carries — so only that one save can be
    // classified as an echo; a real edit typed right after an undo (while the
    // store's 500 ms `isInRedoUndo` window is still open) records history.
    const armed = undoRedoEchoRef?.current != null;
    const isEcho = armed && undoRedoEchoRef?.current === content;
    if (undoRedoEchoRef) undoRedoEchoRef.current = null;
    // `isInRedoUndoRef` is the fallback for the one case content equality
    // cannot see: Froala's own `html.set` normalisation (block wrapping, empty
    // tag pruning) making the echo differ from what the undo applied. Only
    // honoured for the armed save, and only while the store still says an
    // undo/redo is in flight.
    const isUndoRedoEcho =
      isEcho || (armed && isInRedoUndoRef?.current === true);

    if (customOnUpdate) customOnUpdate(content);
    else
      dispatch(
        editEntity({
          data: { content },
          id,
          ...(isUndoRedoEcho ? { skipHistory: true } : {}),
        }),
      );
  };
};

export const isEntityPaste = (e: ClipboardEvent) => {
  const data = e.clipboardData?.getData("text");
  if (!data) return false;
  try {
    const parsed = JSON.parse(data);
    return !!parsed?.entityEntryRootId;
  } catch {
    return false;
  }
};
